/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, Component, ReactNode } from "react";

// react-router-dom components
import { Link, Navigate, useNavigate } from "react-router-dom";

// Store
import { useSignInStore } from "stores/useSignInStore";

// Crypto
import CryptoJS from "crypto-js";

// Utils
import { getStoredAccessToken } from "Shared/Utils/CredentialStorage";

// Network
import { request } from "app/network";

// Alert
import Swal from "sweetalert2";

// CSS
import "./SignInBasicScreen.css";

// 테넌트 선택 저장 키
const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
const REMEMBER_ME_KEY = "mediinstep_remember_me";
const SAVED_USER_ID_KEY = "mediinstep_saved_user_id";
const SAVED_PASSWORD_KEY = "mediinstep_saved_password";
const SECRET_KEY = "MediInStepSecretPassKey123!"; // Simple obfuscation key

class SignInBasicScreenClass extends Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      rememberId: false,
      rememberPassword: false,
      userId: "",
      password: "",
      tenants: [],
      selectedTenant: "",
      loadingTenants: false,
      loadingLogin: false,
    };
  }

  componentDidMount(): void {
    // 이미 인증되어 있으면 홈으로 리다이렉트
    const token = getStoredAccessToken();
    if (token) {
      const { navigate } = this.props as any;
      navigate("/dashboards/analytics");
    } else {
      // 테넌트 목록 가져오기
      this.loadTenants();

      // 저장된 아이디/기억하기 불러오기
      // 저장된 아이디/비밀번호/설정 불러오기
      const savedRememberId = localStorage.getItem(REMEMBER_ME_KEY); // Reusing key for ID preference or we can make new one. Let's make new ones for clarity.
      // Actually, let's keep REMEMBER_ME_KEY as "Remember ID" for backward compatibility if possible? 
      // User asked to split. Let's use specific keys.
      const savedRememberIdFlag = localStorage.getItem("mediinstep_remember_id_flag");
      const savedRememberPasswordFlag = localStorage.getItem("mediinstep_remember_password_flag");

      const savedUserId = localStorage.getItem(SAVED_USER_ID_KEY);
      const savedPassword = localStorage.getItem(SAVED_PASSWORD_KEY);

      if (savedRememberIdFlag === "true" && savedUserId) {
        this.setState({
          rememberId: true,
          userId: savedUserId,
        });
      }

      if (savedRememberPasswordFlag === "true" && savedPassword) {
        let decodedPassword = "";
        try {
          // AES Decryption
          const bytes = CryptoJS.AES.decrypt(savedPassword, SECRET_KEY);
          decodedPassword = bytes.toString(CryptoJS.enc.Utf8);
        } catch (e) {
          console.error("Password decode failed", e);
        }
        if (decodedPassword) {
          this.setState({
            rememberPassword: true,
            password: decodedPassword,
          });
        }
      }
    }
  }

  loadTenants = async (): Promise<void> => {
    try {
      this.setState({ loadingTenants: true });
      const response = await request("Tenant.list", { paramz: {} });

      if (response && response.data) {
        const tenants = Array.isArray(response.data) ? response.data : [];

        // 저장된 테넌트 선택값 가져오기
        const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

        // 저장된 값이 있고, 해당 테넌트가 목록에 존재하는지 확인
        let defaultTenant = "";
        if (savedTenantId && tenants.some((t: any) =>
          (t.seqId?.toString() === savedTenantId) || (t.id === savedTenantId)
        )) {
          // 저장된 값이 유효하면 사용
          defaultTenant = savedTenantId;
        } else if (tenants.length > 0) {
          // 저장된 값이 없거나 유효하지 않으면 id가 "default"인 테넌트를 찾아서 선택
          const defaultIdTenant = tenants.find((t: any) => t.id === "default");
          if (defaultIdTenant) {
            // id가 "default"인 테넌트를 기본 선택값으로 설정
            defaultTenant = defaultIdTenant.seqId?.toString() || defaultIdTenant.id || "";
          } else {
            // id가 "default"인 테넌트가 없으면 첫 번째 테넌트 선택
            defaultTenant = tenants[0].seqId?.toString() || tenants[0].id || "";
          }
          // 선택한 테넌트를 localStorage에 저장
          localStorage.setItem(SELECTED_TENANT_KEY, defaultTenant);
        }

        this.setState({
          tenants,
          selectedTenant: defaultTenant,
        });
      }
    } catch (error) {
      console.error("테넌트 목록 조회 실패:", error);
      // 에러가 발생해도 로그인은 가능하도록 빈 배열로 설정
      this.setState({ tenants: [] });
    } finally {
      this.setState({ loadingTenants: false });
    }
  }

  handleTenantChange = (value: string): void => {
    // 선택한 테넌트를 localStorage에 저장
    localStorage.setItem(SELECTED_TENANT_KEY, value);
    this.setState({ selectedTenant: value });
  }
  handleSetRememberId = (): void => {
    const nextRememberId = !this.state.rememberId;
    this.setState({
      rememberId: nextRememberId,
      // If we are turning OFF remember ID, we must also turn OFF remember Password
      ...(nextRememberId === false && { rememberPassword: false }),
    });
  }

  handleSetRememberPassword = (): void => {
    const nextRememberPassword = !this.state.rememberPassword;
    this.setState({
      rememberPassword: nextRememberPassword,
      // If we are turning ON remember Password, we must also turn ON remember ID
      ...(nextRememberPassword === true && { rememberId: true }),
    });
  }

  login = async (): Promise<void> => {
    const { navigate } = this.props as any;
    const { userId, password } = this.state as any;

    // useSignInStore 사용
    const { setUserId, setPassword, login: loginAction } = useSignInStore.getState();

    setUserId(userId);
    setPassword(password);

    this.setState({ loadingLogin: true });

    await loginAction((isFail: boolean, errorMessage?: string) => {
      this.setState({ loadingLogin: false });
      if (!isFail) {
        // 아이디 기억하기 처리
        if (this.state.rememberId) {
          localStorage.setItem("mediinstep_remember_id_flag", "true");
          localStorage.setItem(SAVED_USER_ID_KEY, userId);
        } else {
          localStorage.removeItem("mediinstep_remember_id_flag");
          localStorage.removeItem(SAVED_USER_ID_KEY);
        }

        // 비밀번호 기억하기 처리
        if (this.state.rememberPassword) {
          localStorage.setItem("mediinstep_remember_password_flag", "true");
          // AES Encryption
          const encryptedPassword = CryptoJS.AES.encrypt(password, SECRET_KEY).toString();
          localStorage.setItem(SAVED_PASSWORD_KEY, encryptedPassword);
        } else {
          localStorage.removeItem("mediinstep_remember_password_flag");
          localStorage.removeItem(SAVED_PASSWORD_KEY);
        }

        // 로그인 성공 시 홈으로 이동
        navigate("/dashboards/analytics");
      } else {
        // 로그인 실패 시 alert 표시
        Swal.fire({
          icon: "error",
          title: "로그인 실패",
          text: errorMessage || "아이디 또는 비밀번호가 올바르지 않습니다.",
          confirmButtonText: "확인",
        });
      }
    });
  }
  render() {
    const { rememberId, rememberPassword, userId, password, tenants, selectedTenant, loadingTenants } = this.state as any;

    return (
      <div className="mediinstep-login-page">
        {/* Ambient Background */}
        <div className="ambient-bg">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
        </div>
        <div className="noise"></div>

        {/* Main Content */}
        <div className="glass-container">
          {/* Left: Presentation Panel */}
          <div className="content-panel">
            <div className="brand-header">
              <div className="brand-logo">
                <img src="/favicon.png" alt="MediInStep" />
              </div>
              <div className="brand-title-group">
                <span className="brand-name">메디인스텝</span>
                <span className="brand-company">MediStep Health Care. Inc.</span>
              </div>
            </div>

            <div className="copy-section">
              <h1 className="main-headline">
                매일 걷는 습관이<br />건강한 삶의 시작입니다.
              </h1>
              <p className="sub-headline">
                <strong>메디인스텝</strong>은 걷기, 운동, 재활을 하나의 앱으로 관리하고,<br />
                건강한 활동을 포인트로 보상받는 프리미엄 헬스케어 플랫폼입니다.
              </p>

              <div className="features-grid">
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2A10 10 0 1 0 22 12 10 10 0 0 0 12 2z" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <h3>스마트 걷기 기록</h3>
                    <p>실시간 걸음수, 이동거리 자동 측정 및 일/월별 통계 제공</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                      <line x1="7" y1="2" x2="7" y2="22" />
                      <line x1="17" y1="2" x2="17" y2="22" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <line x1="2" y1="7" x2="7" y2="7" />
                      <line x1="2" y1="17" x2="7" y2="17" />
                      <line x1="17" y1="17" x2="22" y2="17" />
                      <line x1="17" y1="7" x2="22" y2="7" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <h3>전문 운동·재활 영상</h3>
                    <p>전문가가 제공하는 맞춤 영상 시청 시 리워드 자동 지급</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="7" />
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <h3>포인트 적립 & 챌린지</h3>
                    <p>매월 목표 달성 시 추가 포인트 및 다양한 혜택 교환</p>
                  </div>
                </div>
              </div>

              <div className="target-audience">
                <span className="target-pill">🏃 꾸준한 운동 습관 형성</span>
                <span className="target-pill">🩺 재활 및 보행 운동</span>
                <span className="target-pill">🎁 건강 관리와 보상</span>
                <span className="target-pill">👵 시니어 건강관리</span>
              </div>
            </div>
          </div>

          {/* Right: Login Panel */}
          <div className="login-panel">
            <div className="login-wrapper">
              <div className="login-header">
                <h2>관리자 로그인</h2>
                <p>플랫폼 관리를 위해 로그인해 주세요.</p>
              </div>

              <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label className="form-label">테넌트 센터 선택</label>
                  <div className="select-wrapper">
                    <select
                      className="form-control form-select"
                      value={selectedTenant}
                      onChange={(e) => this.handleTenantChange(e.target.value)}
                      disabled={loadingTenants || tenants.length === 0}
                    >
                      {tenants.map((tenant: any) => (
                        <option key={tenant.seqId || tenant.id} value={tenant.seqId?.toString() || tenant.id || ""}>
                          {tenant.name || tenant.id || `테넌트 ${tenant.seqId || tenant.id}`}
                        </option>
                      ))}
                    </select>
                    <div className="select-icon-wrapper">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">관리자 아이디</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ID를 입력하세요"
                    value={userId}
                    onChange={(e) => this.setState({ userId: e.target.value })}
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">비밀번호</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => this.setState({ password: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (!loadingTenants && tenants.length > 0 && !this.state.loadingLogin && userId.length >= 4 && password.length >= 4) {
                          this.login();
                        }
                      }
                    }}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-options">
                  <label className="checkbox-wrapper">
                    <input type="checkbox" checked={rememberId} onChange={this.handleSetRememberId} />
                    <div className="checkmark">
                      <svg viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <span className="checkbox-text">아이디 기억하기</span>
                  </label>

                  <label className="checkbox-wrapper">
                    <input type="checkbox" checked={rememberPassword} onChange={this.handleSetRememberPassword} />
                    <div className="checkmark">
                      <svg viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <span className="checkbox-text">비밀번호 기억하기</span>
                  </label>
                </div>

                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => this.login()}
                  disabled={loadingTenants || tenants.length === 0 || this.state.loadingLogin || userId.length < 4 || password.length < 4}
                >
                  {this.state.loadingLogin ? "로그인 중..." : "로그인 하기"}
                </button>
              </form>

              <div className="login-footer">
                문제가 있으신가요? <Link to="/public/inquiry">문의하기</Link>
              </div>
              <div className="login-footer-terms">
                <Link to="/public/terms/privacy">개인정보처리방침</Link>
                <span className="footer-divider">·</span>
                <Link to="/public/terms/service">이용약관</Link>
              </div>
              <div className="login-copyright">
                © 2026, MediStep Health Care. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function SignInBasicScreen(props: any) {
  const navigate = useNavigate();
  const token = getStoredAccessToken();

  // 이미 인증되어 있으면 홈으로 리다이렉트
  if (token) {
    return <Navigate to="/dashboards/analytics" replace />;
  }

  return <SignInBasicScreenClass {...props} navigate={navigate} />;
}

export default SignInBasicScreen;
